import { Target, X } from "lucide-react";
import { useState } from "react";
import { DonutChart, type DonutSlice } from "@/components/charts/DonutChart";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { OriginLink } from "@/components/shared/origin-link";
import { PageHeader } from "@/components/shared/page-header";
import { SearchInput } from "@/components/shared/search-input";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { ViewToggle } from "@/components/shared/view-toggle";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type AspirationReport, useAspirationReport } from "@/hooks/use-person-aspirations";
import { useViewMode } from "@/hooks/use-view-mode";

type ReportTab = "by-person" | "by-role";
const TABS: TabItem[] = [
  { key: "by-person", label: "By person" },
  { key: "by-role", label: "By role" },
];

// Fixed categorical order for the donut (identity per role); overflow folds into
// a neutral "Other" slice rather than cycling the ramp.
const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];
const DONUT_MAX = 5; // distinct slices before folding into "Other"

type RoleGroup = AspirationReport["byRole"][number];

/** A donut slice paired with the role ids it covers (for click-to-filter). */
interface SliceMeta {
  label: string;
  roleIds: Set<string>;
}

/** Build the donut slices (top N + "Other") and the role-id set behind each. */
function buildDonut(byRole: RoleGroup[]): { slices: DonutSlice[]; meta: SliceMeta[] } {
  const ranked = [...byRole].sort((a, b) => b.people.length - a.people.length);
  if (ranked.length <= DONUT_MAX) {
    return {
      slices: ranked.map((r, i) => ({
        label: r.roleName,
        value: r.people.length,
        color: CHART_COLORS[i % CHART_COLORS.length] ?? "var(--chart-1)",
      })),
      meta: ranked.map((r) => ({ label: r.roleName, roleIds: new Set([r.roleId]) })),
    };
  }
  const top = ranked.slice(0, DONUT_MAX - 1);
  const rest = ranked.slice(DONUT_MAX - 1);
  const otherLabel = `${rest.length} other roles`;
  return {
    slices: [
      ...top.map((r, i) => ({
        label: r.roleName,
        value: r.people.length,
        color: CHART_COLORS[i] ?? "var(--chart-1)",
      })),
      {
        label: otherLabel,
        value: rest.reduce((sum, r) => sum + r.people.length, 0),
        color: "var(--muted-foreground)",
      },
    ],
    meta: [
      ...top.map((r) => ({ label: r.roleName, roleIds: new Set([r.roleId]) })),
      { label: otherLabel, roleIds: new Set(rest.map((r) => r.roleId)) },
    ],
  };
}

/** The "#n" priority marker shown inside report pills (1 = first choice). */
function RankBadge({ rank }: { rank: number }) {
  return <span className="font-semibold text-3xs text-primary">#{rank}</span>;
}

/**
 * A role pill linking to the role detail, with the target career level (when
 * one was named) and the person's priority rank.
 */
function RolePill({
  roleId,
  roleName,
  roleLevelName,
  rank,
}: {
  roleId: string;
  roleName: string;
  roleLevelName: string | null;
  rank: number;
}) {
  return (
    <OriginLink
      to="/dashboard/roles/$roleId"
      params={{ roleId }}
      className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs transition-colors hover:bg-accent"
    >
      {roleName}
      {roleLevelName && <span className="text-muted-foreground">· {roleLevelName}</span>}
      <RankBadge rank={rank} />
    </OriginLink>
  );
}

/**
 * A person pill linking to the person detail. `rank` is where this role sits
 * in that person's own priority list; `roleLevelName` is the rung they're
 * aiming at, which differs person to person within one role's group.
 */
function PersonPill({
  personId,
  personName,
  roleLevelName,
  rank,
}: {
  personId: string;
  personName: string;
  roleLevelName?: string | null;
  rank: number;
}) {
  return (
    <OriginLink
      to="/dashboard/persons/$personId"
      params={{ personId }}
      className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs transition-colors hover:bg-accent"
    >
      {personName}
      {roleLevelName && <span className="text-muted-foreground">· {roleLevelName}</span>}
      <RankBadge rank={rank} />
    </OriginLink>
  );
}

/** The report body (only mounted once data is present, so state is simple). */
function ReportBody({ report }: { report: AspirationReport }) {
  const [tab, setTab] = useState<ReportTab>("by-person");
  const [view, setView] = useViewMode("aspirations.view");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search, 200).trim().toLowerCase();

  const { slices, meta } = buildDonut(report.byRole);
  const selected = selectedIndex != null ? (meta[selectedIndex] ?? null) : null;

  // Clicking a slice filters to its role(s) and jumps to the people list; a
  // second click on the same slice clears the filter.
  function selectSlice(index: number) {
    setSelectedIndex((prev) => {
      const next = prev === index ? null : index;
      if (next != null) setTab("by-person");
      return next;
    });
  }

  const bySlice = {
    byPerson: selected
      ? report.byPerson.filter((p) => p.roles.some((r) => selected.roleIds.has(r.roleId)))
      : report.byPerson,
    byRole: selected ? report.byRole.filter((r) => selected.roleIds.has(r.roleId)) : report.byRole,
  };
  // The whole report is already client-side, so search is a plain filter:
  // person names on the By person tab, role names on the By role tab.
  const byPerson = query
    ? bySlice.byPerson.filter((p) => p.personName.toLowerCase().includes(query))
    : bySlice.byPerson;
  const byRole = query
    ? bySlice.byRole.filter((r) => r.roleName.toLowerCase().includes(query))
    : bySlice.byRole;
  const noMatches = query !== "" && (tab === "by-person" ? byPerson : byRole).length === 0;

  return (
    <>
      <div className="rounded-lg border bg-card p-4 sm:p-6">
        <h2 className="font-semibold text-foreground text-sm">People aspiring per role</h2>
        <p className="mt-0.5 text-muted-foreground text-xs">
          Select a slice to see the people aspiring to it.
        </p>
        <DonutChart
          className="mt-4 max-w-xl"
          slices={slices}
          title="Share of aspirations by role"
          centerLabel="people"
          onSelect={selectSlice}
          selectedIndex={selectedIndex}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 border-b">
          <TabBar
            tabs={TABS}
            value={tab}
            onChange={(k) => setTab(k as ReportTab)}
            className="border-b-0"
          />
          <ViewToggle value={view} onChange={setView} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder={tab === "by-person" ? "Search people…" : "Search roles…"}
            className="w-full sm:max-w-xs"
          />
          {selected && (
            <button
              type="button"
              onClick={() => setSelectedIndex(null)}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-medium text-primary text-xs transition-colors hover:bg-primary/15"
            >
              {selected.label}
              <X className="size-3" />
            </button>
          )}
        </div>

        {noMatches ? (
          <p className="rounded-lg border bg-card px-4 py-8 text-center text-muted-foreground text-sm">
            No {tab === "by-person" ? "people" : "roles"} match “{search.trim()}”.
          </p>
        ) : tab === "by-person" ? (
          view === "card" ? (
            <ul className="space-y-3">
              {byPerson.map((p) => (
                <li key={p.personId} className="rounded-lg border bg-card p-4">
                  <OriginLink
                    to="/dashboard/persons/$personId"
                    params={{ personId: p.personId }}
                    className="font-semibold text-sm transition-colors hover:text-brand-blue"
                  >
                    {p.personName}
                  </OriginLink>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.roles.map((r) => (
                      <RolePill
                        key={r.id}
                        roleId={r.roleId}
                        roleName={r.roleName}
                        roleLevelName={r.roleLevelName}
                        rank={r.rank}
                      />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-card">
              <ul className="divide-y">
                {byPerson.map((p) => (
                  <li
                    key={p.personId}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <OriginLink
                      to="/dashboard/persons/$personId"
                      params={{ personId: p.personId }}
                      className="min-w-0 shrink-0 truncate font-medium text-sm transition-colors hover:text-brand-blue"
                    >
                      {p.personName}
                    </OriginLink>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {p.roles.map((r) => (
                        <RolePill
                          key={r.id}
                          roleId={r.roleId}
                          roleName={r.roleName}
                          roleLevelName={r.roleLevelName}
                          rank={r.rank}
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : view === "card" ? (
          <ul className="space-y-3">
            {byRole.map((r) => (
              <li key={r.roleId} className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between gap-2">
                  <OriginLink
                    to="/dashboard/roles/$roleId"
                    params={{ roleId: r.roleId }}
                    className="font-semibold text-sm transition-colors hover:text-brand-blue"
                  >
                    {r.roleName}
                  </OriginLink>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-semibold text-3xs text-muted-foreground">
                    {r.people.length} interested
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {r.people.map((p) => (
                    <PersonPill
                      key={p.id}
                      personId={p.personId}
                      personName={p.personName}
                      roleLevelName={p.roleLevelName}
                      rank={p.rank}
                    />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <ul className="divide-y">
              {byRole.map((r) => (
                <li key={r.roleId} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="flex min-w-0 shrink-0 items-center gap-2">
                    <OriginLink
                      to="/dashboard/roles/$roleId"
                      params={{ roleId: r.roleId }}
                      className="truncate font-medium text-sm transition-colors hover:text-brand-blue"
                    >
                      {r.roleName}
                    </OriginLink>
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-semibold text-3xs text-muted-foreground">
                      {r.people.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {r.people.map((p) => (
                      <PersonPill
                        key={p.id}
                        personId={p.personId}
                        personName={p.personName}
                        roleLevelName={p.roleLevelName}
                        rank={p.rank}
                      />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Manager/admin report of the roles employees have flagged as aspirational.
 * Scope is decided server-side (admins see everyone; a non-admin manager sees
 * only their direct reports), so this page just renders whatever it's handed.
 */
export function AspirationsReportPage() {
  useSetBreadcrumbs([{ label: "People & Organization" }, { label: "Role Aspirations" }]);
  const { data, isLoading, isError, error, refetch } = useAspirationReport();

  const description =
    data?.scope === "manager"
      ? "Roles your direct reports have flagged they want to work toward."
      : "Roles employees have flagged they want to work toward.";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="People & Organization"
        title="Role Aspirations"
        description={description}
      />

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState message={error?.message ?? "Failed to load report"} onRetry={() => refetch()} />
      ) : !data || data.byPerson.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No aspirations flagged"
          description={
            data?.scope === "manager"
              ? "None of your direct reports have flagged a role yet."
              : "No one has flagged a role to work toward yet."
          }
        />
      ) : (
        <ReportBody report={data} />
      )}
    </div>
  );
}
